import _ from 'lodash';

import productService, {
  getFittingBracket,
} from '../../../../services/products';
import geometry from '../../../../scene/util/geometry';
import { config as asConfig } from '../../../../scene/boaxel/AdjustableConfig';
import tacHelpers from '../../tacHelpers';
import constants from '../../../../settings/constants';
import getMountingRails from './mountingRail';
import { causedBracketChanges, fitBracket } from './brackets';
import { flatten, unique } from '../../../../util/array';
import { ITEMS } from '../../../../constants';

const sectionWidthsCache = [];

function getIntactUprights(old, curr, enforceColor = false) {
  return old.uprights.filter(oldUpright =>
    curr.uprights.find(
      upright =>
        upright.itemid === oldUpright.itemid &&
        (!enforceColor || upright.filter?.color === oldUpright.filter?.color)
    )
  );
}

function hasAllUprightsIntact(old, curr, enforceColor = false) {
  const intactUprights = getIntactUprights(old, curr, enforceColor);
  return (
    intactUprights.length === old.uprights.length &&
    intactUprights.length === curr.uprights.length
  );
}

function hasIntactUprightsOnBothSides(old, curr) {
  return (
    getIntactUprights(old, curr).reduce((acc, curr) => {
      if (!acc.length || !acc.find(prev => prev.x === curr.x)) {
        acc.push(curr);
      }
      return acc;
    }, []).length >= 2
  );
}

function sameX(a, b) {
  return a.x === b.x;
}

function sameY(a, b) {
  return a.y === b.y;
}

function sameWidth(a, b) {
  return a.width === b.width;
}

function sameHeight(a, b) {
  return a.height === b.height;
}

function totallyIntact(old, curr) {
  return (
    (!old.uprights || hasAllUprightsIntact(old, curr, true)) &&
    sameX(old, curr) &&
    sameY(old, curr) &&
    sameWidth(old, curr) &&
    sameHeight(old, curr)
  );
}

function sameRightmostPos(a, b) {
  return a.x + a.width === b.x + b.width;
}

function hasMovedOneGridStepInX(a, b) {
  const gridStep = constants.DYNAMIC_GRID[constants.DRAG_MODE.FLOAT].x.step;
  return [gridStep, -gridStep].some(
    stepDistance =>
      a.x - b.x === stepDistance &&
      a.x + a.width - stepDistance === b.x + b.width
  );
}

function hasMovedAdjustableRangeInX(old, curr) {
  const maxMove = asConfig.section.maxWidth - asConfig.section.minWidth;
  const move = Math.abs(old.x - curr.x);
  return move <= maxMove;
}

function hasMovedAdjustableToFixedRangeInX(old, curr, prio) {
  if (!prio) {
    return false;
  }
  const move = Math.abs(old.x - curr.x);
  return (
    [600, 800].some(width => width === prio.width + move) ||
    prio.width - move === asConfig.section.minWidth
  );
}

function wasConsequentlyMoved(old, curr, prio) {
  return (
    hasMovedOneGridStepInX(old, curr) ||
    hasMovedAdjustableRangeInX(old, curr) ||
    hasMovedAdjustableToFixedRangeInX(old, curr, prio)
  );
}

function wasResized(old, curr) {
  if (!old.logic.extendable) {
    return false;
  }
  if (
    (old.y === curr.y && old.x > curr.x && old.width < curr.width) ||
    (old.x < curr.x && old.width > curr.width)
  ) {
    return true;
  }
}

function partiallyIntact(old, curr, prio) {
  return (
    (!old.uprights || hasIntactUprightsOnBothSides(old, curr)) &&
    ((old.height > 0 && geometry.collides(old, curr)) || sameY(old, curr)) &&
    (sameX(old, curr) ||
      sameRightmostPos(old, curr) ||
      wasResized(old, curr) ||
      wasConsequentlyMoved(old, curr, prio))
  );
}

function findOldSections(sectionsInTac, curr, prio) {
  if (sectionsInTac.find(old => totallyIntact(old, curr))) {
    return [];
  }
  return sectionsInTac.filter(old => partiallyIntact(old, curr, prio));
}

function findOldSectionToUpdate(sectionsInTac, curr, prio) {
  if (
    prio &&
    geometry.collides(prio, curr) &&
    hasIntactUprightsOnBothSides(prio, curr)
  ) {
    //If our current section shares at least two uprights with our prio, we want to update that section.
    return prio;
  }
  const oldSections = findOldSections(sectionsInTac, curr, prio);
  // if were only setting height, then look at same place
  const unmovedSection = oldSections.find(
    cand => cand.x === curr.x && cand.y === curr.y && cand.width === curr.width
  );
  if (unmovedSection) {
    return unmovedSection;
  }
  const oldSection =
    oldSections.length && geometry.closestCollidingRect(curr, oldSections, 1);
  return oldSection;
}

function belongsToSection(gItem, newSection, allSections, tac) {
  const padding = geometry.uniformPadding(1);

  const gBrackets = newSection.uprights
    .filter(unique)
    .map(up => tacHelpers.getItem(tac, up.itemid))
    .map(upright => {
      const bracket = fitBracket(upright, gItem, getFittingBracket(gItem));
      if (!bracket) {
        return null;
      }
      const gBracket = {
        ...bracket,
        x: bracket.x + upright.x,
        y: bracket.y + upright.y,
        z: bracket.z + upright.z,
      };
      return gBracket;
    })
    .filter(Boolean);

  if (gBrackets.length !== 2) {
    // all items need two brackets
    return false;
  }

  const gBracket = geometry.closest(
    gBrackets.filter(gBracket => {
      return geometry.collides(gItem, gBracket, padding);
    }),
    gItem
  );

  if (newSection.y + newSection.height < gBracket?.y + gBracket?.height) {
    // the closest fitting bracket would be in an invalid place
    return false;
  }

  const collider = {
    ...gItem,
    y: gBracket?.y || gItem.y,
    height: 1,
  };

  const candidates = allSections.filter(
    section =>
      newSection.depth === section.depth && geometry.collides(section, gItem)
  );

  if (candidates.length < 2) {
    return geometry.collides(newSection, collider);
  }

  if (geometry.closestCollidingRect(collider, candidates) === newSection) {
    // this is where it belongs
    return true;
  }

  // consider items that are half a snab distance narrower than a section to be part of it
  const maxDiff = constants.DYNAMIC_GRID[constants.DRAG_MODE.FLOAT].x.step / 2;
  return (
    gItem.width + maxDiff > newSection.width &&
    geometry.contains(newSection, collider, padding)
  );
}

function fitsInSectionY(gItem, newSection) {
  return newSection.uprights.every(upright =>
    upright.items.every(bracket => bracket.y >= 0)
  );
}

function fitsInSectionX(gItem, newSection) {
  return sameWidth(gItem, newSection);
}

function getInvalidItems(newSection, allSections, allChildren, tac) {
  const invalidItems = [];
  for (let i = 0; i < allChildren.length; i++) {
    const child = allChildren[i];
    const gItem = tacHelpers.getGlobalCoords(child, tac);
    const belongs = belongsToSection(gItem, newSection, allSections, tac);
    const fits =
      fitsInSectionX(gItem, newSection) && fitsInSectionY(gItem, newSection);

    if (!belongs || !fits) {
      invalidItems.push({
        ...child,
        gy: gItem.y,
        gx: gItem.x,
        keepDuringDrag: true,
      });
    }
  }

  return invalidItems;
}

function removeDuplicateItems(targetArray, others) {
  const washed = [];
  [...targetArray].forEach(section => {
    section.items = section.items.filter(
      item =>
        !item.keepDuringDrag ||
        !others.some(
          section =>
            section.items.length &&
            section.items.find(
              other => other.itemid === item.itemid && !other.keepDuringDrag
            )
        )
    );
    washed.push(section);
  });

  return washed;
}
function validDistance(distance) {
  const availableWidths = sectionWidthsCache.length
    ? sectionWidthsCache
    : productService.getSections().map(section => section.width);

  if (!sectionWidthsCache.length) {
    sectionWidthsCache.push(...availableWidths);
  }

  if (availableWidths.some(width => width === distance)) {
    return true;
  }
  if (
    availableWidths.find(width => width === asConfig.section.minWidth) &&
    distance >= asConfig.section.minWidth &&
    distance <= asConfig.section.maxWidth
  ) {
    return true;
  }
  return false;
}

function getAllValidUprightConnections(uprights) {
  const connections = [];
  // Get all connections of uprights placed at a valid distance from each other
  for (let i = 0; i < uprights.length; i++) {
    for (let j = i; j < uprights.length; j++) {
      const distance = Math.abs(uprights[i].x - uprights[j].x);
      if (validDistance(distance)) {
        const connectingRect = geometry.connectingRect(
          uprights[i],
          uprights[j]
        );

        if (connectingRect.height > 0 && connectingRect.width > 0) {
          connectingRect.uprights = [uprights[i], uprights[j]];
          connections.push(connectingRect);
        }
      }
    }
  }

  return connections;
}

function mergeSections(unmerged) {
  const sectionRects = [...unmerged];

  // Merge same-width sections with no vertical gap in-between
  let i = sectionRects.length;
  while (i--) {
    let j = i;
    while (j--) {
      const potentialMerge =
        i !== j && sectionRects[i].width === sectionRects[j].width;
      if (potentialMerge) {
        const intersection = geometry.connectingRect(
          sectionRects[i],
          sectionRects[j]
        );
        const mergeable =
          intersection.height === 0 &&
          [sectionRects[i].width, sectionRects[j].width].every(
            width => width === intersection.width
          );
        const merged =
          mergeable &&
          geometry.mergeSiblings(sectionRects[i], sectionRects[j], true);
        if (merged) {
          merged.uprights = [
            ...sectionRects[i].uprights,
            ...sectionRects[j].uprights,
          ];
          sectionRects.splice(i, 1);
          sectionRects.splice(j, 1, merged);
          i--;
        }
      }
    }
  }

  return sectionRects;
}

function getMainCollisions(section, collisions) {
  return collisions.reduce((acc, curr) => {
    if (acc.contained) {
      return acc;
    }
    const collisionRect = geometry.connectingRect(curr, section);
    const contained =
      sameHeight(curr, collisionRect) &&
      geometry.contains(section, collisionRect);

    if (contained) {
      return { ...acc, contained: collisionRect };
    } else if (
      collisionRect.y === section.y &&
      (!acc.bottom || collisionRect.height > acc.bottom.height)
    ) {
      return {
        ...acc,
        bottom: collisionRect,
      };
    } else if (
      collisionRect.y + collisionRect.height === section.y + section.height &&
      (!acc.top || collisionRect.height > acc.top.height)
    ) {
      return {
        ...acc,
        top: collisionRect,
      };
    }

    return acc;
  }, {});
}

function splitSection(section, rects, index, collisionRect) {
  const sections = [...rects];
  const split = [{ ...section }, { ...section }];

  split[0].height = collisionRect.y - split[0].y;
  split[1].y = collisionRect.y + collisionRect.height;
  split[1].height -= split[0].height + collisionRect.height;

  sections.splice(index, 1);
  sections.push(split[0], split[1]);
  return sections;
}

function splitSections(rects, uprightsInTac) {
  let i = rects.length;
  while (i--) {
    const section = rects[i];
    if (section.height <= 0) {
      continue;
    }
    const otherUprights = uprightsInTac.filter(
      upright =>
        !section.uprights
          .map(upright => upright.itemid)
          .some(itemid => itemid === upright.itemid)
    );

    // 800 section is 820
    if (section.width > 900) {
      // large sections are allowed to overlap, both smaller and each other
      continue;
    }
    // If this section collides with other upright inside it, we need to adjust the section accordingly
    const collisions = geometry.getCollidingRects(section, otherUprights);
    if (!collisions.length) {
      continue;
    }

    const mainCollisions = getMainCollisions(section, collisions);

    if (mainCollisions.contained) {
      const collisionRect = mainCollisions.contained;
      rects = splitSection(section, rects, i, collisionRect);
      i = rects.length;
    } else {
      if (mainCollisions.top) {
        section.height -= mainCollisions.top.height;
      }

      if (mainCollisions.bottom) {
        section.y += mainCollisions.bottom.height;
        section.height -= mainCollisions.bottom.height;
      }
    }
  }

  return rects.map(section => {
    return {
      ...section,
      uprights: section.uprights
        .map(upright =>
          tacHelpers.getItem({ items: uprightsInTac }, upright.itemid)
        )
        .filter(upright =>
          geometry.collides(section, upright, { front: 10, back: 10 })
        ),
    };
  });
}
function getSlotSources(item, tac) {
  return item.uprights
    ? item.uprights
        .map(upright => tacHelpers.getItem(tac, upright.itemid))
        .filter(upright => upright.x < item.x)
    : [];
}

function getSectionProducts(rects) {
  const uprightDepth = 20;

  return rects.map(sectionRect => {
    const { x, y, width, height, uprights } = sectionRect;
    const fullSection = {
      ...productService.getFit(
        { filter: { type: ITEMS.SECTION } },
        {
          width: width - productService.getPostWidth(),
        }
      ),
      x: x + productService.getPostWidth() / 2,
      y,
      z: uprightDepth,
      height,
      uprights,
      items: [],
    };

    return fullSection;
  });
}

function getUntouchedSections(sectionsInTac, sections) {
  return (
    sectionsInTac.filter(oldSection =>
      sections.find(newSection => totallyIntact(oldSection, newSection))
    ) || []
  );
}

function getNewSections(sectionsInTac, sections, prio) {
  return (
    sections.filter(
      newSection =>
        !sectionsInTac.find(
          oldSection =>
            totallyIntact(oldSection, newSection) ||
            partiallyIntact(oldSection, newSection, prio)
        )
    ) || []
  );
}

function getUpdatedSections(
  sectionsInTac,
  sections,
  priorityItem,
  tac,
  isPersistent
) {
  return (
    sections
      .filter(newSection =>
        findOldSectionToUpdate(sectionsInTac, newSection, priorityItem)
      )
      .map(newSection => {
        const oldSection = findOldSectionToUpdate(
          sectionsInTac,
          newSection,
          priorityItem
        );
        const slotSources = getSlotSources(newSection, tac);

        // update items in sections
        const oldSections = findOldSections(
          sectionsInTac,
          newSection,
          priorityItem
        ).sort((a, b) => a.y - b.y);

        const allChildren = flatten(oldSections.map(section => section.items));

        const keepAll =
          priorityItem &&
          wasConsequentlyMoved(oldSection, newSection, priorityItem);
        const myChildren = keepAll
          ? allChildren
          : allChildren
              .map(item => {
                const gItem = tacHelpers.getGlobalCoords(item, tac);
                const belongs = belongsToSection(
                  gItem,
                  newSection,
                  sections,
                  tac
                );
                const fits = fitsInSectionY(gItem, newSection);

                return (
                  belongs && fits && { ...item, y: gItem.y - newSection.y }
                );
              })
              .filter(Boolean);

        newSection.items =
          tacHelpers.getNewChildren(
            { items: myChildren },
            newSection,
            {
              type: ITEMS.SECTION,
              switchingProp: 'width',
              width: newSection.width,
            },
            { slotSources }
          ) || [];

        if (!isPersistent) {
          // get articles from oldSection that are now not fitting in new section
          const invalidItems = getInvalidItems(
            newSection,
            sections,
            allChildren.filter(
              child =>
                !newSection.items.some(item => item.itemid === child.itemid)
            ),
            tac
          );

          // add them to new section because they are going to be dimmed
          invalidItems.forEach(item => {
            const updatedItem = {
              ...item,
              y: item.gy - newSection.y,
              x: item.gx - newSection.x,
            };
            newSection.items.push(updatedItem);
          });
        }

        return {
          ...oldSection,
          ...newSection,
        };
      }) || []
  );
}

function getDuplicateSections(updated) {
  let duplicates = [];
  if (updated.length > 1) {
    const uniques = _.uniqBy(updated, 'itemid');
    if (uniques.length !== updated.length) {
      duplicates = updated.filter(
        item => !uniques.find(unique => unique === item)
      );
    }
  }
  return duplicates;
}

function getRemovedSections(sectionsInTac, untouched, added, updated) {
  return (
    sectionsInTac
      .filter(
        oldSection =>
          ![...untouched, ...added, ...updated].some(
            section => section.itemid === oldSection.itemid
          )
      )
      .map(section => {
        return {
          ...section,
          items: section.items.map(item => ({ ...item, keepDuringDrag: true })),
          keepDuringDrag: true,
        };
      }) || []
  );
}

function addSectionDiff(tac, options, diff) {
  const { priorityItem, isPersistent } = options;
  const sectionsInTac = tacHelpers.getSections(tac);
  const uprightsInTac = tac.items.filter(
    item =>
      productService.isType(item, ITEMS.UPRIGHT) &&
      tacHelpers.isWithinWall(item, tac)
  );
  let sectionRects = getAllValidUprightConnections(uprightsInTac);

  //Sort sections to prepare them for merge
  sectionRects.sort((a, b) => a.x - b.x || a.width - b.width || a.y - b.y);

  sectionRects = mergeSections(sectionRects);
  sectionRects = splitSections(sectionRects, uprightsInTac);

  //Remove any invalid sections still left
  sectionRects = sectionRects.filter(rect => rect.height > 0);

  const sections = getSectionProducts(sectionRects);

  const untouched = getUntouchedSections(sectionsInTac, sections);
  const added = getNewSections(sectionsInTac, sections, priorityItem);

  let updated = getUpdatedSections(
    sectionsInTac,
    sections,
    priorityItem,
    tac,
    isPersistent
  );

  const keepers = [...untouched, ...added, ...updated];
  updated = removeDuplicateItems(updated, keepers);

  const duplicates = getDuplicateSections(updated);

  if (duplicates.length) {
    updated = updated.filter(
      item => !duplicates.find(duplicate => duplicate === item)
    );
    added.push(
      ...duplicates.map(duplicate => {
        return { ...duplicate, itemid: null };
      })
    );
  }

  let removed = getRemovedSections(sectionsInTac, untouched, added, updated);
  removed = removeDuplicateItems(removed, keepers);

  diff.added.push(...added);
  diff.updated.push(...updated);
  diff.removed.push(...removed);
  diff.untouched.push(...untouched);

  return diff;
}

function addMountingRailDiff(tac, diff) {
  //Remove all mounting rails, since these will be re-added
  const removed = [
    ...tac.items.filter(item => productService.isType(item, 'mounting-rail')),
  ];

  const added = [];
  if (
    productService.areMountingRailsValid() &&
    tac.wall &&
    !tac.settings.disableMountingRails
  ) {
    added.push(...getMountingRails(tac));
  }

  diff.added.push(...added);
  diff.removed.push(...removed);

  return diff;
}

function addBracketDiff(tac, diff) {
  const bracketDiff = causedBracketChanges(tac);

  bracketDiff.added.forEach(upright => {
    const brackets = upright.items
      .filter(item => productService.isType(item, 'bracket'))
      .map(bracket => ({ ...bracket, parentRef: upright.itemid }));
    diff.added.push(...brackets);
  });

  bracketDiff.removed.forEach(upright => {
    const brackets = upright.items
      .filter(item => productService.isType(item, 'bracket'))
      .map(bracket => ({ ...bracket, parentRef: upright.itemid }));
    diff.removed.push(...brackets);
  });

  return diff;
}

function getDependencyDiff(tac, options = {}) {
  let diff = { added: [], updated: [], removed: [], untouched: [] };

  if (
    options.triggerItem &&
    (productService.isInsert(options.triggerItem) ||
      productService.isType(options.triggerItem, ITEMS.CLOTHES_RAIL))
  ) {
    if (!options.keepBrackets) {
      diff = addBracketDiff(tac, diff);
    }
  } else {
    diff = addSectionDiff(tac, options, diff);
    const validSections = [...diff.untouched, ...diff.added, ...diff.updated];

    const modifiedTac = {
      ...tac,
      items: [...tac.items.filter(item => !productService.isSection(item))],
    };

    modifiedTac.items.push(...validSections);

    diff = addMountingRailDiff(modifiedTac, diff);

    if (!options.keepBrackets) {
      diff = addBracketDiff(modifiedTac, diff);
    }
  }

  return diff;
}

function getDependentItems(item, tac) {
  if (productService.isType(item, ITEMS.UPRIGHT)) {
    return tac.items.filter(item =>
      productService.isType(item, ['mounting-rail', ITEMS.SECTION])
    );
  }
  return [];
}

export default { getDependencyDiff, getDependentItems, getSlotSources };

export { getDependencyDiff, getDependentItems, getSlotSources };
